/**
 * The Gallery's curated stack recipes (M2 PR-4, #245; docs/product/personas.md
 * "Scaffolds: where the Founder story and the agentic workbench converge").
 *
 * This is a STATIC, in-repo catalog — no server changes, no fetching. Each
 * recipe carries a prefilled `compose.yml` string that the existing
 * compose-import flow (`ImportComposeModal`) accepts as-is: the same
 * bounded, allow-listed subset the server parses (`server/internal/composeio`),
 * hand-checked against it here rather than duplicating that parser.
 *
 * Secrets (S-12/S-13): a recipe never inlines a credential. Where a service
 * needs one, the compose references it by NAME using the server's actual
 * whole-value reference syntax, `${secret:NAME}` (ADR-0020 §2a) — the same
 * form `server/internal/composeio/secretsyntax.go` parses and
 * `server/internal/deployplan/secretref.go` resolves at deploy time. A
 * recipe's `secrets` list documents which names it expects so the operator
 * knows what to create in Secrets before deploying; the compose text itself
 * never carries a value.
 */

/** One secret a recipe's compose references by name (never by value). */
export interface RecipeSecretRef {
  /** The exact name used in the compose's `${secret:NAME}` reference. */
  name: string;
  /** Plain-language note on what this secret is for, shown next to the name
   * — never a suggested or example value (S-13). */
  note: string;
}

export interface Recipe {
  /** Stable, URL-safe id — used as the catalog key and by the DeployWizard. */
  id: string;
  name: string;
  /** One or two sentences, written for someone deciding whether this is the
   * stack they want, not a feature list. */
  description: string;
  /** Short labels for scanning the gallery at a glance (not a filter UI in
   * this v0 — see the PR description for what's deferred). */
  tags: string[];
  /** A valid compose.yml, prefilled into ImportComposeModal on "Deploy this
   * stack." Kept realistic and minimal: an image, ports, a couple of env
   * vars, and depends_on where it matters — not a production-grade example. */
  compose: string;
  /** Secrets this recipe's compose references by name. Omitted (or empty)
   * for recipes that need none. */
  secrets?: RecipeSecretRef[];
}

export const RECIPES: Recipe[] = [
  {
    id: "postgres-api-worker",
    name: "Postgres + API + worker",
    description:
      "A web API backed by Postgres, plus a background worker sharing the same database — the shape of most real backends.",
    tags: ["postgres", "api", "worker"],
    compose: `services:
  api:
    image: your-registry/api:latest
    ports:
      - "8080:8080"
    environment:
      PORT: "8080"
      DATABASE_URL: \${secret:database_url}
    depends_on:
      - postgres

  worker:
    image: your-registry/worker:latest
    environment:
      DATABASE_URL: \${secret:database_url}
    depends_on:
      - postgres

  postgres:
    image: postgres:16
    environment:
      POSTGRES_DB: appdb
      POSTGRES_USER: app
      POSTGRES_PASSWORD: \${secret:postgres_password}
`,
    secrets: [
      { name: "database_url", note: "Full connection string the api and worker use to reach postgres." },
      { name: "postgres_password", note: "Password for the postgres service's own POSTGRES_PASSWORD." },
    ],
  },
  {
    id: "static-site",
    name: "Static site",
    description: "A single nginx container serving a static site — the smallest possible starting point.",
    tags: ["static", "nginx"],
    compose: `services:
  web:
    image: nginx:1.27
    ports:
      - "80:80"
`,
  },
  {
    id: "n8n-automation",
    name: "n8n workflow automation",
    description: "Self-hosted n8n for building and running workflow automations.",
    tags: ["automation", "n8n"],
    compose: `services:
  n8n:
    image: n8nio/n8n:latest
    ports:
      - "5678:5678"
    environment:
      N8N_HOST: "0.0.0.0"
      N8N_PROTOCOL: "http"
      N8N_ENCRYPTION_KEY: \${secret:n8n_encryption_key}
`,
    secrets: [
      {
        name: "n8n_encryption_key",
        note: "Encrypts n8n's stored workflow credentials — generate a random value, don't reuse one from elsewhere.",
      },
    ],
  },
  {
    id: "redis-api",
    name: "Redis-backed API",
    description: "A web API with a Redis cache or queue alongside it — a common pairing for sessions, rate limits, or jobs.",
    tags: ["redis", "api"],
    compose: `services:
  api:
    image: your-registry/api:latest
    ports:
      - "8080:8080"
    environment:
      REDIS_URL: \${secret:redis_url}
    depends_on:
      - redis

  redis:
    image: bitnami/redis:7.2
    environment:
      REDIS_PASSWORD: \${secret:redis_password}
`,
    secrets: [
      { name: "redis_url", note: "Full connection URL (including credentials) the api uses to reach redis." },
      { name: "redis_password", note: "Password redis itself requires — REDIS_PASSWORD on the bitnami image." },
    ],
  },
];

/** Looks up a recipe by id — used by the DeployWizard to resolve a
 * pre-selected recipe from the Gallery. */
export function findRecipe(id: string): Recipe | undefined {
  return RECIPES.find((recipe) => recipe.id === id);
}
